<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" class="relative z-50" @close="close">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/25" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel class="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
              <DialogTitle as="h3" class="text-lg font-medium leading-6 text-gray-900">
                {{ title }}
              </DialogTitle>
              
              <div class="mt-4 prose prose-sm max-h-[60vh] overflow-y-auto">
                <div v-html="content"></div>
              </div>

              <div class="mt-6 flex justify-end">
                <button
                  type="button"
                  class="inline-flex justify-center rounded-md border border-transparent bg-primary-100 px-4 py-2 text-sm font-medium text-primary-900 hover:bg-primary-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                  @click="close"
                >
                  Fechar
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  TransitionRoot,
  TransitionChild,
  Dialog,
  DialogPanel,
  DialogTitle,
} from '@headlessui/vue'

const props = defineProps<{
  isOpen: boolean
  type: 'privacy' | 'terms'
}>()

const emit = defineEmits<{
  close: []
}>()

const title = computed(() => {
  return props.type === 'privacy' ? 'Política de Privacidade' : 'Termos de Uso'
})

const content = computed(() => {
  if (props.type === 'privacy') {
    return `
      <h2>Política de Privacidade</h2>
      <p>A sua privacidade é importante para nós. É política da AutoVolt respeitar a sua privacidade em relação a qualquer informação sua que possamos coletar.</p>
      
      <h3>1. Informações que coletamos</h3>
      <p>Coletamos informações quando você:</p>
      <ul>
        <li>Se registra em nosso site</li>
        <li>Faz uma compra</li>
        <li>Inscreve-se em nossa newsletter</li>
        <li>Responde a uma pesquisa</li>
        <li>Preenche um formulário</li>
      </ul>

      <h3>2. Como usamos suas informações</h3>
      <p>Podemos usar as informações que coletamos para:</p>
      <ul>
        <li>Personalizar sua experiência</li>
        <li>Melhorar nosso site</li>
        <li>Melhorar nosso atendimento ao cliente</li>
        <li>Processar transações</li>
        <li>Enviar emails periódicos</li>
      </ul>

      <h3>3. Como protegemos suas informações</h3>
      <p>Implementamos uma variedade de medidas de segurança para manter a segurança de suas informações pessoais.</p>
    `
  }
  
  return `
    <h2>Termos de Uso</h2>
    <p>Ao acessar o site AutoVolt, você concorda em cumprir estes termos de serviço, todas as leis e regulamentos aplicáveis e concorda que é responsável pelo cumprimento de todas as leis locais aplicáveis.</p>
    
    <h3>1. Licença de Uso</h3>
    <p>É concedida permissão para usar temporariamente o software AutoVolt em um computador pessoal, telefone celular ou tablet, exclusivamente para fins pessoais ou comerciais.</p>

    <h3>2. Restrições</h3>
    <p>Você não pode:</p>
    <ul>
      <li>Copiar ou modificar o software</li>
      <li>Tentar extrair o código fonte</li>
      <li>Tentar criar trabalhos derivados</li>
      <li>Revender ou redistribuir o software</li>
    </ul>

    <h3>3. Rescisão</h3>
    <p>Suas obrigações e direitos sob estes termos continuarão após o término de seu uso do software.</p>
  `
})

const close = () => {
  emit('close')
}
</script>